import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sprout, ArrowLeft, Printer, Loader2, CheckCircle, X, Minus } from 'lucide-react';
import { getSingleCentralRecord } from '../services/api';

const SCRPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchSCR = useCallback(async () => {
    try {
      const res = await getSingleCentralRecord();
      setData(res.data);
    } catch (err) {
      console.error('Failed to load SCR:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSCR(); }, [fetchSCR]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#5BA4A4]" />
      </div>
    );
  }

  const records = data?.records || [];

  return (
    <div className="min-h-screen bg-white">
      {/* Print-hidden header */}
      <header className="bg-white border-b border-gray-200 print:hidden sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/onboarding')} data-testid="back-to-onboarding">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="text-lg font-bold text-[#2D4A4A]">Single Central Record</h1>
          </div>
          <Button
            onClick={() => window.print()}
            className="bg-[#5BA4A4] hover:bg-[#4A9090] text-white"
            data-testid="print-scr-btn"
          >
            <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
          </Button>
        </div>
      </header>

      {/* Printable Content */}
      <main className="max-w-[1400px] mx-auto px-4 py-6 print:px-2 print:py-0">
        {/* SCR Header */}
        <div className="text-center mb-6 print:mb-4">
          <div className="flex justify-center mb-2 print:hidden">
            <div className="w-12 h-12 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-xl flex items-center justify-center">
              <Sprout className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 className="text-xl font-bold text-[#2D4A4A] print:text-lg" data-testid="scr-title">
            Single Central Record
          </h1>
          <p className="text-base font-semibold text-[#5BA4A4] print:text-sm">{data?.organisation}</p>
          <p className="text-xs text-gray-500 mt-1">
            Generated: {data?.generated_at ? new Date(data.generated_at).toLocaleDateString('en-GB', {
              day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }) : ''}
            {' | '}{data?.total_staff} staff member{data?.total_staff !== 1 ? 's' : ''}
          </p>
        </div>

        {/* SCR Table */}
        <div className="overflow-x-auto border border-gray-300 rounded-lg print:border-black print:rounded-none">
          <table className="w-full text-[11px] print:text-[9px] border-collapse" data-testid="scr-table">
            <thead>
              <tr className="bg-[#2D4A4A] text-white print:bg-gray-800">
                <th className="p-2 text-left font-semibold border-r border-[#3D5A5A] print:border-gray-600 sticky left-0 bg-[#2D4A4A] print:bg-gray-800 z-10 min-w-[120px]">Staff Member</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[60px]">Status</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[55px]">ID Check</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[70px]">Right to Work</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[100px]">DBS</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[55px]">Refs</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[55px]">Employ. History</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[55px]">Quals</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[55px]">Safeguard Decl.</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[55px]">Criminal Decl.</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[55px]">Health</th>
                <th className="p-2 text-center font-semibold border-r border-[#3D5A5A] min-w-[55px]">Policies</th>
                <th className="p-2 text-center font-semibold min-w-[55px]">Bank</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, idx) => (
                <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-[#F0FAF7] print:hover:bg-transparent border-b border-gray-200`}>
                  {/* Name */}
                  <td className={`p-2 border-r border-gray-200 sticky left-0 z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <p className="font-semibold text-[#2D4A4A] leading-tight">{r.name}</p>
                    <p className="text-[9px] text-gray-400 leading-tight">{r.start_date}</p>
                  </td>

                  {/* Overall Status */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <StatusBadge status={r.overall_status} />
                  </td>

                  {/* ID */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <Check ok={r.id_verified} />
                    {r.id_type && <p className="text-[9px] text-gray-400 mt-0.5 capitalize">{r.id_type.replace('_', ' ')}</p>}
                  </td>

                  {/* RTW */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <Check ok={r.rtw_verified} />
                    {r.rtw_status && <p className="text-[9px] text-gray-400 mt-0.5 capitalize">{r.rtw_status.replace('_', ' ')}</p>}
                    {r.rtw_expiry && <p className="text-[9px] text-amber-600 mt-0.5">Exp: {formatDate(r.rtw_expiry)}</p>}
                  </td>

                  {/* DBS */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <Check ok={r.dbs_verified} />
                    {r.dbs_number && <p className="text-[9px] text-gray-500 mt-0.5">#{r.dbs_number}</p>}
                    {r.dbs_type && <p className="text-[9px] text-gray-400 capitalize">{r.dbs_type.replace('_', ' ')}</p>}
                    {r.dbs_expiry && <p className="text-[9px] text-amber-600">Exp: {formatDate(r.dbs_expiry)}</p>}
                    {r.dbs_update_service && <p className="text-[9px] text-emerald-600">Update Svc</p>}
                  </td>

                  {/* References */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <Check ok={r.references_verified} />
                    <p className="text-[9px] text-gray-400 mt-0.5">{r.references_count} ref{r.references_count !== 1 ? 's' : ''}</p>
                  </td>

                  {/* Employment */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <Check ok={r.employment_verified} />
                  </td>

                  {/* Qualifications */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <Check ok={r.qualifications_verified} />
                    {r.highest_qual && <p className="text-[9px] text-gray-400 mt-0.5 truncate max-w-[80px]" title={r.highest_qual}>{r.highest_qual}</p>}
                  </td>

                  {/* Safeguarding */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <Check ok={r.safeguarding_declared && r.safeguarding_signed} />
                  </td>

                  {/* Criminal */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <Check ok={r.criminal_declared} />
                  </td>

                  {/* Health */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <Check ok={r.health_declared} />
                  </td>

                  {/* Policies */}
                  <td className="p-2 text-center border-r border-gray-200">
                    <Check ok={r.policies_agreed && r.policies_signed} />
                  </td>

                  {/* Bank */}
                  <td className="p-2 text-center">
                    <Check ok={r.bank_details_provided} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-[10px] text-gray-500 print:mt-2">
          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-600" /> Verified/Approved</span>
          <span className="flex items-center gap-1"><X className="w-3 h-3 text-red-500" /> Not Complete</span>
          <span className="flex items-center gap-1"><Minus className="w-3 h-3 text-gray-400" /> Not Started</span>
        </div>

        {/* Print footer */}
        <div className="mt-6 text-center text-[10px] text-gray-400 print:mt-3">
          <p>This Single Central Record is generated by the Growing Minds Tuition Compliance Platform.</p>
          <p>Document is for internal use only. Store securely in compliance with UK GDPR.</p>
        </div>
      </main>

      {/* Print styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          @page { size: landscape; margin: 10mm; }
          .print\\:hidden { display: none !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
        }
      `}</style>
    </div>
  );
};

const Check = ({ ok }) => {
  if (ok === true) return <CheckCircle className="w-4 h-4 text-emerald-600 mx-auto" />;
  if (ok === false) return <X className="w-4 h-4 text-red-400 mx-auto" />;
  return <Minus className="w-4 h-4 text-gray-300 mx-auto" />;
};

const StatusBadge = ({ status }) => {
  const config = {
    fully_approved: { label: 'Cleared', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    ready_for_approval: { label: 'Ready', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
    in_review: { label: 'Review', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    in_progress: { label: 'Progress', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
    not_started: { label: 'None', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
  };
  const c = config[status] || config.not_started;
  return <Badge className={`text-[9px] px-1.5 py-0 ${c.cls}`}>{c.label}</Badge>;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
  } catch {
    return dateStr;
  }
};

export default SCRPage;
